import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { ModalComponent } from "..";
import { ProfileType } from "../../../redux/features/auth/auth.type";
import {
  useAddCooperativeBankDetailsMutation,
  useAddMemberBankDetailsMutation,
} from "../../../redux/features/payment/payment.api";
import {
  useGetBankListQuery,
  useResolveAccountNumberQuery,
} from "../../../redux/features/settings/settings.api";
import { CooperativeBankAccountSchema } from "../../../validationSchema";
import { InformationCard } from "../../cards/infoCard";
import ComboBox from "../../inputs/combobox";
import { TextInput } from "../../inputs/textInput";

export const AddBankAccountModal = ({
  modalOpen,
  onModalClose = () => {},
  onSuccess = () => {},
  type,
}: {
  modalOpen: boolean;
  onModalClose?: () => void;
  onSuccess?: () => void;
  type: ProfileType;
}) => {
  const [searchValues, setSearchValues] = useState<{
    bankName: string;
  }>({
    bankName: "",
  });

  const { data: bankList, isFetching: fetchingBanks } = useGetBankListQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
      skip: !modalOpen,
    }
  );

  const [addCoopAccount, { isLoading: addingCoopAccount }] =
    useAddCooperativeBankDetailsMutation();
  const [addMemberAccount, { isLoading: addingMemberAccount }] =
    useAddMemberBankDetailsMutation();

  const {
    values,
    errors,
    isValid,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
    resetForm,
    validateForm,
  } = useFormik({
    initialValues: {
      bankName: "",
      bankNameSec: "",
      bankCode: "",
      accountNumber: "",
      accountName: "",
    },
    validationSchema: CooperativeBankAccountSchema,
    onSubmit: async (data) => {
      const payload = {
        bankCode: data.bankCode,
        bankName: data.bankName,
        accountNumber: data.accountNumber,
        accouuntName: data.accountName,
      };

      if (type === "cooperative") {
        const resp = await addCoopAccount(payload);

        if (resp.data?.success) {
          resetForm();
          onSuccess();
        }
      } else {
        const resp = await addMemberAccount(payload);

        if (resp.data?.success) {
          resetForm();
          onSuccess();
        }
      }
    },
  });

  const {
    data: resolvedResponse,
    isFetching: resolvingDetails,
    isSuccess: resolvedSuccess,
  } = useResolveAccountNumberQuery(
    {
      bankCode: values.bankCode,
      accountNumber: values.accountNumber,
    },
    {
      refetchOnMountOrArgChange: true,
      skip:
        !!errors.bankCode ||
        !!errors.accountNumber ||
        !values.bankCode ||
        !values.accountNumber ||
        !modalOpen,
    }
  );

  useEffect(() => {
    if (resolvedSuccess && resolvedResponse.data) {
      setFieldValue("accountName", resolvedResponse.data.account_name);
      setFieldValue("accountNumber", resolvedResponse.data.account_number);
      setTimeout(() => {
        validateForm();
      }, 500);
    }
    // eslint-disable-next-line
  }, [resolvedResponse, resolvedSuccess]);

  return (
    <ModalComponent
      isModalOpen={modalOpen}
      onModalClose={() => {
        onModalClose();
        resetForm();
      }}
      title="Add Bank Account"
      subTitle={undefined}
      modalClassName=""
      childrenClassName="mt-[8px]"
      hasButtons
      proceedButtonText="Add account"
      onProceedClick={handleSubmit}
      isLoading={addingCoopAccount || addingMemberAccount}
      isProceedDisabled={!isValid || addingCoopAccount || addingMemberAccount}
    >
      <InformationCard
        info={
          type === "cooperative"
            ? "Kindly ensure you add the bank account that is tied to your cooperative financial activities"
            : "Kindly ensure you add your personal bank account."
        }
        size="mini"
        variant="purple"
      />

      <form className="mt-4 flex flex-col gap-[12px]" onSubmit={handleSubmit}>
        <ComboBox
          type="text"
          isDropdown
          name="bankNameSec"
          value={values?.bankNameSec}
          menuItems={bankList?.data
            ?.filter((c) => {
              const s = searchValues.bankName.toLowerCase();

              return c.name.toLowerCase().includes(s);
            })
            ?.map((c) => ({
              name: c.name,
              value: {
                name: c.name,
                value: c.code,
              },
            }))}
          placeholder="Select Bank"
          onChange={(e) => {
            handleChange(e);
            setFieldValue("bankName", undefined);
            setFieldValue("bankCode", undefined);
          }}
          onMenuClose={() => {
            setSearchValues((prev) => ({
              ...prev,
              bankName: "",
            }));
          }}
          onBlur={handleBlur}
          hasIcon={false}
          errors={
            errors?.bankName && touched.bankNameSec ? errors?.bankName : ""
          }
          menuClassName="!max-h-[250px] !min-w-[200px] !z-[999]"
          position="bottom start"
          onSearch={(searchValue) => {
            setSearchValues((prev) => ({
              ...prev,
              bankName: searchValue,
            }));
          }}
          onSelectOption={(val: { name: string; value: string }) => {
            setSearchValues((prev) => ({ ...prev, dialCode: "" }));
            setFieldValue("bankName", val.name);
            setFieldValue("bankNameSec", val.name);
            setFieldValue("bankCode", val.value);
          }}
          className="!z-[999]"
          hasTitle={false}
          containerClassName="!w-full !z-[999]"
          immediate
          loading={fetchingBanks}
        />

        <TextInput
          type="text"
          name="accountNumber"
          value={values?.accountNumber}
          placeholder={
            type === "cooperative"
              ? "Cooperative Account Number"
              : "Account Number"
          }
          onChange={handleChange}
          onBlur={handleBlur}
          hasIcon={false}
          errors={
            touched?.accountNumber && errors?.accountNumber
              ? errors?.accountNumber
              : ""
          }
          autoComplete="off"
          disabled={resolvingDetails}
        />

        {!errors?.accountNumber &&
        !errors?.bankName &&
        !!values.bankCode &&
        !!values.accountNumber ? (
          <TextInput
            name="accountName"
            type="text"
            value={
              resolvingDetails
                ? "Retrieving account name..."
                : values?.accountName
            }
            placeholder={
              values?.accountName
                ? "Account Name"
                : "Failed to resolve account number"
            }
            onChange={handleChange}
            onBlur={handleBlur}
            hasIcon={false}
            errors={undefined}
            inputClass={`
            text-pry-green-200
            font-medium ${
              !values?.accountName && "placeholder:!text-error-200"
            }`}
            className={`!py-2 ani-drop ${
              !resolvedSuccess && "!bg-light-red-bg"
            }`}
            readOnly={true}
            disabled={true}
          />
        ) : undefined}
      </form>
    </ModalComponent>
  );
};
